import { computed, ref } from 'vue'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { trackError } from '@/utils/error-tracker'

// Storage keys for persistence
const REDIRECT_ROUTE_KEY = 'mining_pool_redirect_route'
const LAST_ATTEMPTED_ROUTE_KEY = 'mining_pool_last_attempted_route'

// Reactive state for redirect management
const savedRedirectRoute = ref<RouteLocationRaw | null>(null)
const lastAttemptedRoute = ref<RouteLocationNormalized | null>(null)

/**
 * Save the last attempted route for redirect after login
 */
export function saveLastAttemptedRoute(route: RouteLocationNormalized): void {
  // Don't save auth routes or 2FA routes
  if (route.meta?.guest === true || route.meta?.verify2fa === true) {
    return
  }

  const routeToSave = {
    name: route.name,
    path: route.path,
    params: route.params,
    query: route.query,
    hash: route.hash
  }

  // Save to reactive state
  lastAttemptedRoute.value = route
  savedRedirectRoute.value = routeToSave

  // Persist to localStorage for browser refresh scenarios
  try {
    localStorage.setItem(REDIRECT_ROUTE_KEY, JSON.stringify(routeToSave))
    localStorage.setItem(LAST_ATTEMPTED_ROUTE_KEY, JSON.stringify(routeToSave))
  } catch (error) {
    trackError(
      error instanceof Error ? error : new Error('Failed to save redirect route'),
      {
        component: 'RouteUtils',
        action: 'saveLastAttemptedRoute',
        additional: { routeName: route.name, routePath: route.path }
      }
    )
  }
}

/**
 * Clear saved redirect route
 * (Internal function used by composable)
 */
function clearSavedRoute(): void {
  savedRedirectRoute.value = null
  lastAttemptedRoute.value = null

  try {
    localStorage.removeItem(REDIRECT_ROUTE_KEY)
    localStorage.removeItem(LAST_ATTEMPTED_ROUTE_KEY)
  } catch (error) {
    trackError(
      error instanceof Error ? error : new Error('Failed to clear redirect route'),
      {
        component: 'RouteUtils',
        action: 'clearSavedRoute'
      }
    )
  }
}

/**
 * Restore redirect route from localStorage
 * (Internal function used by composable)
 */
function restoreRedirectRoute(): RouteLocationRaw | null {
  try {
    const stored = localStorage.getItem(REDIRECT_ROUTE_KEY)
    if (stored) {
      const route = JSON.parse(stored) as RouteLocationRaw
      savedRedirectRoute.value = route
      return route
    }
  } catch (error) {
    trackError(
      error instanceof Error ? error : new Error('Failed to restore redirect route'),
      {
        component: 'RouteUtils',
        action: 'restoreRedirectRoute'
      }
    )
  }

  return null
}

/**
 * Composable for handling redirects after login
 */
export function useRedirectAfterLogin() {
  // Computed to get the redirect route
  const redirectRoute = computed(() => {
    // First check reactive state
    if (savedRedirectRoute.value) {
      return savedRedirectRoute.value
    }

    // Then try to restore from localStorage
    return restoreRedirectRoute()
  })

  /**
   * Get the route to redirect to after login
   */
  const getRedirectRoute = (): RouteLocationRaw | null => {
    const route = redirectRoute.value

    // Validate the route still makes sense
    if (route && typeof route === 'object' && 'path' in route) {
      // Don't redirect to auth routes
      if (route.path?.includes('login') ||
        route.path?.includes('signup') ||
        route.path?.includes('verify-2fa')) {
        return null
      }
    }

    return route
  }

  /**
   * Perform redirect after successful login
   */
  const performRedirect = (router: any, fallbackRoute: RouteLocationRaw = { name: 'statistics' }) => {
    const route = getRedirectRoute()

    if (route) {
      // Clear the saved route since we're using it
      clearSavedRoute()

      // Navigate to the saved route
      return router.push(route)
    }

    // No saved route, use fallback
    return router.push(fallbackRoute)
  }

  /**
   * Check if there's a pending redirect
   */
  const hasPendingRedirect = computed(() => {
    return redirectRoute.value !== null
  })

  return {
    redirectRoute: computed(() => redirectRoute.value),
    getRedirectRoute,
    performRedirect,
    hasPendingRedirect,
    clearSavedRoute
  }
}

/**
 * Route meta type extensions for better TypeScript support
 */
declare module 'vue-router' {
  interface RouteMeta {
    // Auth-related
    public?: boolean
    protected?: boolean
    requiresAuth?: boolean
    guest?: boolean
    verify2fa?: boolean

    // Layout and presentation
    title?: string
    description?: string
    layout?: 'default' | 'auth' | 'minimal'

    // Permissions
    requiresPermission?: string
    adminOnly?: boolean

    // Behavior
    keepAlive?: boolean
    transition?: string
  }
} 